import { getDeviceId } from '@/util/device-id';
import { DbCollection, StorageKey, shiritoriRef, singleValueAsync } from './firebase-db';
import { onValue, runTransaction, serverTimestamp, update } from 'firebase/database';
import { getDownloadURL, getStorage, ref as storageRef } from 'firebase/storage';
import type { GameDetailsDto, GameDto, TurnTimerStateDto } from '@/game/core/game';
import { ShiritoriFirebaseApp } from './firebase-app';
import { ANY_WORD_LIST_ID, type KanjiLookup, type ShiritoriWordDto, type ShiritoriWordGroup, type WordList } from '@/game/core/shiritori-word';
import type { Player } from '@/game/core/player';
import type { TurnDto } from '@/game/core/turn';

const imageUrlCache = new Map<string, string>();

export async function getImageUrl(imageSet: string, imageId: string) {
    const storageKey = `${StorageKey.ImageSet}/${imageSet}/${imageId}.webp`;

    const cached = imageUrlCache.get(storageKey);
    if (cached) {
        return cached;
    }

    try {
        const storage = getStorage(ShiritoriFirebaseApp);
        const ref = storageRef(storage, storageKey);
        const url = await getDownloadURL(ref);

        imageUrlCache.set(storageKey, url);
        return url;
    } catch (error) {
        console.error('[getImageUrl]', { imageId, imageSet, error });
        return '';
    }
}

export class ShiritoriGameDb {
    deviceId: string;
    gameId: string;

    /** list of words allowed in the game. kana:wordId */
    private _wordListCache: WordList | undefined;

    /** When true, skip list checks and allow any dictionary word */
    private _allowAnyWord = false;

    constructor(gameId: string) {
        this.deviceId = getDeviceId()!;
        this.gameId = gameId;
    }

    get gameRef() {
        return shiritoriRef(`${DbCollection.Game}/${this.gameId}`);
    }

    public async subscribe(callback: (data: GameDto) => void) {
        onValue(this.gameRef, (snapshot) => {
            callback(snapshot.val());
        });
    }

    public async fetchListWords(listId: string) {

        if (listId === ANY_WORD_LIST_ID) {
            this._wordListCache = undefined;
            this._allowAnyWord = true;
            return;
        }

        if (!this._wordListCache) {
            const path = `${DbCollection.ListWordSet}/${listId}`;
            const ref = shiritoriRef(path);
            const words = await singleValueAsync<WordList>(ref);

            this._wordListCache = words || {};
        } else {
            console.log('already cached the word list!');
        }
    }

    public wordBelongsToList(wordKana: string) {
        if (this._allowAnyWord) {
            return true;
        }

        return this._wordListCache !== undefined && !!this._wordListCache[wordKana];
    }

    public async getWordGroup(wordKana: string) {
        const path = `${DbCollection.Word}/${wordKana}`;
        const wordRef = shiritoriRef(path);
        return singleValueAsync<ShiritoriWordGroup>(wordRef);
    }

    public async getKanjiLookup(writtenForm: string) {
        const path = `${DbCollection.KanjiLookup}/${writtenForm}`;
        const lookupRef = shiritoriRef(path);
        return singleValueAsync<KanjiLookup>(lookupRef);
    }

    public async playWord(word: ShiritoriWordDto, player: Player, cellIndex: number) {
        const turnRef = shiritoriRef(`${DbCollection.Game}/${this.gameId}/game/turns/${cellIndex}`);

        const result = await runTransaction(turnRef, (data: TurnDto | null) => {
            if (data) {
                return;
            }

            const turn: TurnDto = {
                index: cellIndex,
                playerId: player.id,
                team: player.team,
                playedAt: serverTimestamp() as unknown as number,
                word: {
                    wordId: word.wordId,
                    kana: word.kana,
                    imageId: word.imageId,
                },
            };

            return turn;
        });

        if (result.committed) {
            await this.clearTurnTimer(cellIndex);
        }
    }

    public async markTurnSeen(turnIndex: number, player: Player, durationSeconds: number) {
        const timerRef = shiritoriRef(`${DbCollection.Game}/${this.gameId}/game/turnTimer`);

        await runTransaction(timerRef, (data: TurnTimerStateDto | null) => {
            const existing = data || null;

            if (existing?.turnIndex === turnIndex && existing.startedAt) {
                return existing;
            }

            const timer: TurnTimerStateDto = {
                turnIndex,
                playerId: player.id,
                team: player.team,
                startedAt: serverTimestamp() as unknown as number,
                durationSeconds,
            };

            return timer;
        });
    }

    public async expireTurnIfElapsed(turnIndex: number) {
        const timerRef = shiritoriRef(`${DbCollection.Game}/${this.gameId}/game/turnTimer`);
        const timer = await singleValueAsync<TurnTimerStateDto | null>(timerRef);
        if (!timer || timer.turnIndex !== turnIndex || !timer.durationSeconds) {
            return;
        }

        const deadline = timer.startedAt + timer.durationSeconds * 1000;
        if (Date.now() < deadline) {
            return;
        }

        const turnRef = shiritoriRef(`${DbCollection.Game}/${this.gameId}/game/turns/${turnIndex}`);
        const result = await runTransaction(turnRef, (data: TurnDto | null) => {
            if (data) {
                return;
            }

            const timedOutTurn: TurnDto = {
                index: turnIndex,
                playerId: timer.playerId,
                team: timer.team,
                playedAt: serverTimestamp() as unknown as number,
                timedOutAt: deadline
            };

            return timedOutTurn;
        });

        if (result.committed) {
            await this.clearTurnTimer(turnIndex);
        }
    }

    private async clearTurnTimer(turnIndex: number) {
        const timerRef = shiritoriRef(`${DbCollection.Game}/${this.gameId}/game/turnTimer`);
        await runTransaction(timerRef, (timer: TurnTimerStateDto | null) => {
            if (timer?.turnIndex === turnIndex) {
                return null;
            }
        });
    }

    public async beginGame() {
        const startedAtRef = shiritoriRef(`${DbCollection.Game}/${this.gameId}/game/startedAt`);

        await runTransaction(startedAtRef, (startedAt: number | null) => {
            if (!startedAt) {
                return serverTimestamp() as unknown as number;
            }
        });
    }

    public async setEndState() {
        const endStateRef = shiritoriRef(`${DbCollection.Game}/${this.gameId}/game/endState`);

        await runTransaction(endStateRef, (endState: GameDetailsDto['endState'] | null) => {
            if (!endState) {
                return {
                    endedAt: serverTimestamp() as unknown as number,
                };
            }
        });
    }

    public async leaveGame() {
        const playerId = this.deviceId;
        const gameData = await singleValueAsync<GameDto | null>(this.gameRef);
        const forfeitingTeam = gameData?.players?.[playerId]?.team;
        const alreadyForfeited = Object.values(gameData?.state || {}).includes('forfeit');

        if (!gameData || !forfeitingTeam || gameData.game?.endState || alreadyForfeited) {
            return;
        }

        const timestamp = serverTimestamp();
        const updates: Record<string, unknown> = {
            [`state/${playerId}`]: 'forfeit',
            'game/endState': {
                endedAt: timestamp,
                reason: 'forfeit',
                forfeitedPlayerId: playerId,
                forfeitedTeam: forfeitingTeam,
            },
        };

        if (!gameData.game?.startedAt) {
            updates['game/startedAt'] = timestamp;
        }
        if (gameData.game?.turnTimer) {
            updates['game/turnTimer'] = null;
        }

        return update(this.gameRef, updates);
    }

    public async getImageUrl(imageSet: string, imageId: string) {
        return getImageUrl(imageSet, imageId);
    }
}
